import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Bed, Bath, Maximize, Phone, ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PropertyDetailPage() {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const res = await axios.get(`${API}/properties/${id}`);
        setProperty(res.data);
      } catch (e) {
        console.error("Failed to fetch property:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchProperty();
  }, [id]);

  if (loading) {
    return (
      <div className="pt-24 pb-16 min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="pt-24 pb-16 min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-beige/60">Property not found</p>
        <Link to="/properties" className="text-gold text-sm hover:text-gold-hover">
          Back to Properties
        </Link>
      </div>
    );
  }

  const typeLabels = { villa: "Villa", flat: "Apartment", land: "Land" };
  const images = property.images?.length > 0 ? property.images : ["https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800"];

  const nextImage = () => setActiveImage((prev) => (prev + 1) % images.length);
  const prevImage = () => setActiveImage((prev) => (prev - 1 + images.length) % images.length);

  return (
    <div data-testid="property-detail-page" className="pt-24 pb-16 min-h-screen">
      {/* Breadcrumb */}
      <div className="px-6 lg:px-8 max-w-7xl mx-auto mb-8">
        <Link
          to="/properties"
          data-testid="back-to-properties"
          className="inline-flex items-center gap-2 text-sm text-beige/50 hover:text-gold transition-colors"
        >
          <ArrowLeft size={14} /> Back to Properties
        </Link>
      </div>

      <div className="px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Image Gallery - 3 cols */}
          <div className="lg:col-span-3">
            {/* Main Image */}
            <div className="relative aspect-[16/10] overflow-hidden rounded-sm mb-4">
              <img
                src={images[activeImage]}
                alt={`${property.title} - Image ${activeImage + 1}`}
                className="w-full h-full object-cover"
                data-testid="property-main-image"
              />

              {images.length > 1 && (
                <>
                  <button
                    data-testid="gallery-prev-button"
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-navy/70 hover:bg-navy/90 text-beige p-2 transition-colors"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    data-testid="gallery-next-button"
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-navy/70 hover:bg-navy/90 text-beige p-2 transition-colors"
                  >
                    <ChevronRight size={20} />
                  </button>
                </>
              )}

              <div className="absolute top-4 left-4">
                <span className="bg-gold text-navy text-xs font-bold tracking-wider uppercase px-3 py-1.5">
                  {typeLabels[property.property_type] || property.property_type}
                </span>
              </div>

              <div className="absolute bottom-4 right-4">
                <span className="bg-navy/70 text-beige text-xs px-3 py-1.5">
                  {activeImage + 1} / {images.length}
                </span>
              </div>
            </div>

            {/* Thumbnails */}
            {images.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-2">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImage(i)}
                    className={`shrink-0 w-20 h-16 overflow-hidden rounded-sm border-2 transition-colors ${
                      i === activeImage ? "border-gold" : "border-transparent opacity-60 hover:opacity-100"
                    }`}
                  >
                    <img src={img} alt={`Thumb ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details - 2 cols */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-3">
              <MapPin size={14} className="text-gold" />
              <span className="text-xs text-beige/60 tracking-wider uppercase">{property.location}</span>
            </div>

            <h1 data-testid="property-title" className="font-heading text-2xl sm:text-3xl tracking-tight text-beige mb-4">
              {property.title}
            </h1>

            <div data-testid="property-price" className="text-gold font-heading text-3xl mb-6">
              {property.price_unit === "Cr" ? "\u20B9" : ""}{property.price} {property.price_unit}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-8 p-4 bg-navy-light border border-white/10 rounded-sm">
              {property.bedrooms != null && (
                <div className="text-center">
                  <Bed size={18} className="text-gold mx-auto mb-1" />
                  <p className="text-lg font-heading text-beige">{property.bedrooms}</p>
                  <p className="text-xs text-beige/50">Bedrooms</p>
                </div>
              )}
              {property.bathrooms != null && (
                <div className="text-center">
                  <Bath size={18} className="text-gold mx-auto mb-1" />
                  <p className="text-lg font-heading text-beige">{property.bathrooms}</p>
                  <p className="text-xs text-beige/50">Bathrooms</p>
                </div>
              )}
              <div className="text-center">
                <Maximize size={18} className="text-gold mx-auto mb-1" />
                <p className="text-lg font-heading text-beige">{property.area}</p>
                <p className="text-xs text-beige/50">{property.area_unit}</p>
              </div>
            </div>

            {/* Description */}
            <div className="mb-8">
              <h3 className="text-xs tracking-[0.2em] uppercase text-gold font-bold mb-3">Description</h3>
              <p data-testid="property-description" className="text-sm text-beige/70 leading-relaxed">
                {property.description}
              </p>
            </div>

            {/* Features */}
            {property.features?.length > 0 && (
              <div className="mb-8">
                <h3 className="text-xs tracking-[0.2em] uppercase text-gold font-bold mb-3">Features</h3>
                <div className="flex flex-wrap gap-2">
                  {property.features.map((feature, i) => (
                    <span
                      key={i}
                      className="text-xs text-beige/70 border border-white/10 px-3 py-1.5 rounded-sm"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="space-y-3 pt-4 border-t border-white/10">
              <Link
                to={`/schedule-visit?property=${property.id}&title=${encodeURIComponent(property.title)}`}
                data-testid="property-schedule-visit-button"
                className="flex items-center justify-center gap-2 w-full bg-gold hover:bg-gold-hover text-navy font-bold text-sm tracking-widest uppercase px-6 py-4 transition-colors duration-300"
              >
                <CalendarDays size={16} /> Schedule Site Visit
              </Link>
              <a
                href={`https://wa.me/918483918695?text=${encodeURIComponent(`Hi, I'm interested in ${property.title} at ${property.location}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="property-whatsapp-button"
                className="flex items-center justify-center gap-2 w-full border border-gold/50 hover:border-gold text-gold text-sm tracking-widest uppercase px-6 py-4 transition-colors duration-300"
              >
                <Phone size={16} /> Enquire on WhatsApp
              </a>
              <Link
                to={`/contact?property=${property.id}&title=${encodeURIComponent(property.title)}`}
                data-testid="property-contact-button"
                className="flex items-center justify-center gap-2 w-full border border-white/10 hover:border-white/30 text-beige/70 text-sm tracking-widest uppercase px-6 py-3 transition-colors duration-300"
              >
                Send Inquiry
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
